/**
 * Authentication Validation Schemas
 *
 * Validation schemas for authentication operations including login,
 * token refresh, and JWT validation.
 *
 * CRITICAL: This file is a 1:1 copy of tenant-api/src/validators/auth.ts
 */

import { z } from 'zod';
import { emailSchema, tenantCodeSchema, passwordSchema } from './common';

// ============================================================================
// LOGIN VALIDATION
// ============================================================================

/**
 * Login request schema
 * Users must provide tenant code, email, and password
 *
 * Authentication flow:
 * 1. Look up tenant by tenantCode
 * 2. Look up user by email (tenant-scoped)
 * 3. Verify password hash
 * 4. Generate JWT token
 */
export const loginRequestSchema = z
  .object({
    tenantCode: tenantCodeSchema,
    email: emailSchema,
    password: z.string({
      required_error: 'Password is required',
      invalid_type_error: 'Password must be a string',
    }).min(1, 'Password cannot be empty'),
  })
  .strict();

export type LoginRequestValidation = z.infer<typeof loginRequestSchema>;

// ============================================================================
// TOKEN REFRESH VALIDATION
// ============================================================================

/**
 * Refresh token schema
 * Used to obtain a new access token using a valid refresh token
 */
export const refreshTokenSchema = z
  .object({
    refreshToken: z
      .string({
        required_error: 'Refresh token is required',
        invalid_type_error: 'Refresh token must be a string',
      })
      .min(1, 'Refresh token cannot be empty'),
  })
  .strict();

export type RefreshTokenValidation = z.infer<typeof refreshTokenSchema>;

// ============================================================================
// JWT TOKEN VALIDATION
// ============================================================================

/**
 * Access token payload schema (15-minute tokens)
 * Includes session_id for session tracking
 */
export const accessTokenPayloadSchema = z
  .object({
    type: z.literal('access'),
    sub: z.string().uuid('Invalid user ID format'),
    tenant_id: z.string().uuid('Invalid tenant ID format'),
    tenant_code: tenantCodeSchema,
    email: emailSchema,
    name: z.string().min(1, 'Name cannot be empty'),
    is_admin: z.boolean(),
    session_id: z.string().uuid('Invalid session ID format'),
    iat: z.number().int().positive('Invalid issued at timestamp'),
    exp: z.number().int().positive('Invalid expiration timestamp'),
    aud: z.string().optional(),
    iss: z.string().optional(),
  })
  .strict();

export type AccessTokenPayloadValidation = z.infer<typeof accessTokenPayloadSchema>;

/**
 * Refresh token payload schema (7-day tokens)
 * Used for obtaining new access tokens
 */
export const refreshTokenPayloadSchema = z
  .object({
    type: z.literal('refresh'),
    sub: z.string().uuid('Invalid user ID format'),
    tenant_id: z.string().uuid('Invalid tenant ID format'),
    session_id: z.string().uuid('Invalid session ID format'),
    token_family: z.string().uuid('Invalid token family format'),
    token_version: z.number().int().nonnegative('Invalid token version'),
    iat: z.number().int().positive('Invalid issued at timestamp'),
    exp: z.number().int().positive('Invalid expiration timestamp'),
    aud: z.string().optional(),
    iss: z.string().optional(),
  })
  .strict();

export type RefreshTokenPayloadValidation = z.infer<typeof refreshTokenPayloadSchema>;

// ============================================================================
// PASSWORD RESET VALIDATION
// ============================================================================

/**
 * Password reset request schema
 * Used to initiate password reset flow
 */
export const passwordResetRequestSchema = z
  .object({
    tenantCode: tenantCodeSchema,
    email: emailSchema,
  })
  .strict();

export type PasswordResetRequestValidation = z.infer<typeof passwordResetRequestSchema>;

/**
 * Password reset confirmation schema
 * Used to complete password reset with token
 */
export const passwordResetConfirmSchema = z
  .object({
    resetToken: z
      .string({
        required_error: 'Reset token is required',
        invalid_type_error: 'Reset token must be a string',
      })
      .min(32, 'Reset token must be at least 32 characters long')
      .max(256, 'Reset token must not exceed 256 characters'),
    newPassword: passwordSchema,
  })
  .strict();

export type PasswordResetConfirmValidation = z.infer<typeof passwordResetConfirmSchema>;

// ============================================================================
// LOGOUT VALIDATION
// ============================================================================

/**
 * Logout request schema (if needed for token blacklisting)
 * Currently optional as JWT is stateless
 */
export const logoutRequestSchema = z
  .object({
    token: z
      .string({
        invalid_type_error: 'Token must be a string',
      })
      .optional(),
  })
  .strict();

export type LogoutRequestValidation = z.infer<typeof logoutRequestSchema>;
